import { useEffect, useMemo, useState } from "react";

import { ArrowLeft, Plus, Search } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  useClassificationGroups,
  useCreateClassificationGroup,
  useDeleteClassificationGroup,
  useUpdateClassificationGroup,
} from "~/hooks/contabilidad/useClassificationGroups";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import type {
  AccountClassificationGroup,
  ClassificationGroupFormData,
} from "~/types/contabilidad";

import { GrupoCuentaDialog } from "./components/GrupoCuentaDialog";
import { GrupoCuentaTable } from "./components/GrupoCuentaTable";

const ALIACE_SPA_ID = "809d3197-9371-4cbb-b544-aadbd29940d6";

export default function GruposCuentaPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<
    AccountClassificationGroup | undefined
  >(undefined);

  const { data: companies = [] } = useCompanies();
  const { data: groups = [], isLoading } = useClassificationGroups({
    company_id: companyFilter !== "all" ? companyFilter : undefined,
  });

  // Set default company to Aliace SPA
  useEffect(() => {
    if (companies.length > 0 && companyFilter === "all") {
      const aliaceSpa = companies.find((c) => c.id === ALIACE_SPA_ID);
      if (aliaceSpa) {
        setCompanyFilter(ALIACE_SPA_ID);
      }
    }
  }, [companies]);

  const createMutation = useCreateClassificationGroup();
  const updateMutation = useUpdateClassificationGroup();
  const deleteMutation = useDeleteClassificationGroup();

  const filteredGroups = useMemo(() => {
    return groups.filter((group) => {
      const matchesSearch =
        !searchQuery ||
        group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.code.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearch;
    });
  }, [groups, searchQuery]);

  const handleCreate = () => {
    setEditingGroup(undefined);
    setDialogOpen(true);
  };

  const handleEdit = (group: AccountClassificationGroup) => {
    setEditingGroup(group);
    setDialogOpen(true);
  };

  const handleSubmit = (data: ClassificationGroupFormData) => {
    if (editingGroup) {
      updateMutation.mutate(
        { id: editingGroup.id, data },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingGroup(undefined);
          },
        }
      );
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          setDialogOpen(false);
        },
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/contabilidad/configuracion">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Grupos de Cuenta</h1>
            <p className="text-sm text-muted-foreground">
              Administre los grupos de clasificacion para estados financieros
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} className="rounded-full">
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Grupo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4 pt-4">
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por codigo o nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 rounded-xl pl-9"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="h-9 w-[200px] rounded-xl">
                <SelectValue placeholder="Filtrar por empresa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las empresas</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <GrupoCuentaTable
            groups={filteredGroups}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <GrupoCuentaDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        group={editingGroup}
        onSubmit={handleSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        defaultCompanyId={companyFilter !== "all" ? companyFilter : ALIACE_SPA_ID}
      />
    </div>
  );
}
